import { Modal, Spin } from 'antd';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { Order } from '../../../services/orders-service/types';
import { useOrderStore } from '../../../store/order-store';
import { StateLoading } from '../../../services/types';
import { useAuthStore } from '../../../store/auth-store';
import { Role } from '../../../services/auth-service/types';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * UploadRawLabDataView props
 */
interface Props {
  order: Order;
}

/**
 * UploadRawLabDataView Component
 */
const UploadRawLabDataView: FunctionComponent<Props> = ({ order }) => {
  const { fetchResultPDF, resultPDF } = useOrderStore();
  const [open, setOpen] = useState<boolean>(false);
  const [delayDisplay, setDelayDisplay] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const { idToken } = useAuthStore();

  useEffect(() => {
    (async () => {
      if (open) {
        await new Promise(res => {
          setTimeout(res, 500);
        });
      }
      setDelayDisplay(open);
    })();
  }, [open]);

  const data = useMemo<StateLoading<string> | undefined>(
    () => resultPDF[`${order?.labResult?.id}`] || {},
    [order?.labResult?.id, resultPDF],
  );

  useEffect(() => {
    (async () => {
      if (order.labResult && open) {
        try {
          await fetchResultPDF(order.labResult);
        } catch (err) {
          setOpen(false);
        }
      }
    })();
  }, [fetchResultPDF, open, order.labResult]);

  const currentLoggedInUser = useMemo(() => idToken?.payload, [idToken]);

  const isEventManager = useMemo(() => {
    if (!currentLoggedInUser?.['custom:roles']) return false;
    const roles = currentLoggedInUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.EventManager];
    let cannotViewLabResults = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        cannotViewLabResults = true;
      }
    });
    return cannotViewLabResults;
  }, [currentLoggedInUser]);

  return (
    <div>
      <div
        className="border p-2 flex flex-row rounded items-center gap-2 bg-white"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
        <div className="flex flex-1">
          <span className="text-black truncate max-w-[400px]">
            {data?.isLoading ? 'Loading...' : 'Uploaded CSV File'}
          </span>
        </div>

        {!isEventManager && (
          <button
            type="button"
            className="bg-gray-200 p-1 px-2 rounded hover:bg-gray-300 cursor-pointer"
            onClick={() => setOpen(true)}>
            <span
              className="text-blue text-xs font-semibold"
              style={{ whiteSpace: 'nowrap' }}>
              View CSV file
            </span>
          </button>
        )}
      </div>
      {data?.error && !data.data ? (
        <span className="text-[#ff0000]">{data.error}</span>
      ) : null}
      <Modal
        centered
        open={open}
        onOk={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        width={750}
        style={{ overflow: 'hidden' }}
        bodyStyle={{ maxHeight: 750 }}
        className="pdf-modal">
        {data?.data && !data?.isLoading && delayDisplay ? (
          <div className="pdf-container" style={{ maxHeight: 650, overflow: 'auto' }}>
            <Document
              file={data.data}
              onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
              loading={<div className="flex justify-center items-center p-4"><Spin /></div>}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  renderTextLayer
                  renderAnnotationLayer
                  width={700}
                />
              ))}
            </Document>
          </div>
        ) : (
          <div className="flex flex-1 justify-center items-center">
            <Spin />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UploadRawLabDataView;
